import React, {MouseEvent} from "react";
import ReactDOM from "react-dom";
import * as _ from "lodash"

import "react-sortable-tree/style.css";
import "./tree.css";
import SortableTree, { walk } from "react-sortable-tree";

import * as SmartDrawings from "../../smartDrawings";

interface ModelStructureProps {
    treeData?: any,
    viewer: Autodesk.Viewing.GuiViewer3D
}

interface ModelStructureState {
    treeData?: any,
    selectedNodes?: number[],
    hiddenNodes?: number[],
    searchString?: string,
    searchFocusIndex?: number,
    searchFoundCount?: number
}

class ModelStructure extends React.Component<ModelStructureProps, ModelStructureState> {

    constructor(props) {
        super(props);
        this.state = {
            treeData: props.treeData,
            selectedNodes: [],
            hiddenNodes: [],
            searchString: "",
            searchFocusIndex: 0,
            searchFoundCount: null
        };
    }

    componentWillMount(): void {
        const { viewer } = this.props;
        viewer.addEventListener(Autodesk.Viewing.HIDE_EVENT, this.hideListener);
        viewer.addEventListener(Autodesk.Viewing.SHOW_EVENT, this.hideListener);
        viewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.selectionListener);
    }

    componentWillUnmount(): void {
        const { viewer } = this.props;
        viewer.removeEventListener(Autodesk.Viewing.HIDE_EVENT, this.hideListener);
        viewer.removeEventListener(Autodesk.Viewing.SHOW_EVENT, this.hideListener);
        viewer.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.selectionListener);
    }

    private hideListener = () => {
        this.setState({ hiddenNodes: this.props.viewer.getHiddenNodes() });
    }

    private selectionListener = () => {
        this.setState({ selectedNodes: this.props.viewer.getSelection() });
    }

    private nodeSelected(rowInfo): boolean {
        return _.some(this.state.selectedNodes, sel => sel === rowInfo.node.id);
    }

    private nodeHidden(rowInfo): boolean {
        return _.some(this.state.hiddenNodes, hid => hid === rowInfo.node.id);
    }

    private hideNode = (rowInfo) => (event: MouseEvent) => {
        if (rowInfo) {
            const leafNodes = this.getLeafNodes(rowInfo.node);
            this.props.viewer.hide([...leafNodes, rowInfo.node.id]);
        }
        event.stopPropagation();
    }

    private showNode = (rowInfo) => (event: MouseEvent) => {
        if (rowInfo) {
            const leafNodes = this.getLeafNodes(rowInfo.node);
            this.props.viewer.show([...leafNodes, rowInfo.node.id]);
        }
        event.stopPropagation();
    }

    private getLeafNodes(parent): number[] {
        if (parent.children.length === 0) {
            return [parent.id];
        }

        const leafNodes: number[] = [];

        walk({
            treeData: [parent],
            getNodeKey: node => node.node.id,
            callback: node => {
                if (node.node.children.length === 0) {
                    leafNodes.push(node.node.id);
                }
            },
            ignoreCollapsed: false
        });
        return leafNodes;
    }

    private selectNode = (rowInfo) => (event: any) => {
        const leafNodes = this.getLeafNodes(rowInfo.node);
        if (leafNodes.length > 0 &&
            !event.target.className.includes("collapseButton") &&
            !event.target.className.includes("expandButton")) {
            this.props.viewer.select([...leafNodes, rowInfo.node.id]);
            this.props.viewer.fitToView(leafNodes);
            event.stopPropagation();
        }
    }

    private customSearchMethod({ node, searchQuery }) {
        return searchQuery && node.title.toLowerCase().indexOf(searchQuery.toLowerCase()) > -1;
    }

    private selectPrevMatch = () => {
        const { searchFocusIndex, searchFoundCount } = this.state;
        this.setState({
            searchFocusIndex:
                searchFocusIndex !== null
                    ? (searchFoundCount + searchFocusIndex - 1) % searchFoundCount
                    : searchFoundCount - 1,
        });
    }

    private selectNextMatch = () => {
        const { searchFocusIndex, searchFoundCount } = this.state;
        this.setState({
            searchFocusIndex:
                searchFocusIndex !== null
                    ? (searchFocusIndex + 1) % searchFoundCount
                    : 0,
        });
    }

    render() {
        const { searchString, searchFoundCount, searchFocusIndex } = this.state;
        return (
            <div>
                <div className="input-group input-group-sm">
                    <div className="input-group-prepend">
                        <span className="input-group-text">
                            <i className="icon ion-md-search" />
                            </span>
                        </div>
                            
                    <input className="form-control" style={_.isEmpty(searchString)
                            ? {}
                            : { borderColor: searchFoundCount > 0 ? "green" : "red" }}
                           placeholder="Search"
                           value={searchString}
                           type="text"
                           onChange={event =>
                                this.setState({ searchString: event.target.value })
                        } />
                    <div className="input-group-append">
                        <span className="input-group-text">
                            {searchFoundCount > 0 ? searchFocusIndex + 1 : 0} / {searchFoundCount}
                        </span>
                    </div>
                    <div className="input-group-append">

                        <button className="btn btn-primary"
                                type="button"
                                disabled={!searchFoundCount}
                                onClick={this.selectPrevMatch}>
                            <i className="icon ion-md-arrow-dropleft"/>
                        </button>

                        <button className="btn btn-primary"
                                type="button"
                                disabled={!searchFoundCount}
                                onClick={this.selectNextMatch}>
                            <i className="icon ion-md-arrow-dropright"/>
                        </button>

                    </div>
                </div>


                <div className="treeContainer">
                    <SortableTree
                        rowHeight={34}
                        scaffoldBlockPxWidth={25}
                        treeData={this.state.treeData}
                        onChange={treeData => this.setState((state, props) => ({ ...state, treeData: treeData }))}
                        canDrag={false}
                        searchMethod={this.customSearchMethod}
                        searchQuery={searchString}
                        searchFocusOffset={searchFocusIndex}
                        searchFinishCallback={matches =>
                            this.setState(state => ({
                                ...state,
                                searchFoundCount: matches.length,
                                searchFocusIndex: matches.length > 0 ? searchFocusIndex % matches.length : 0,
                            }))
                        }
                        onlyExpandSearchedNodes={true}
                        generateNodeProps={rowInfo => {
                            const nodeSelected = this.nodeSelected(rowInfo);
                            const nodeHidden = this.nodeHidden(rowInfo);
                            const nodeProps = {
                                buttons: [
                                    !nodeHidden &&
                                    <button onClick={this.hideNode(rowInfo)}>
                                        <i className="icon ion-md-eye-off"/>
                                    </button>,
                                    nodeHidden &&
                                    <button onClick={this.showNode(rowInfo)}
>
                                        <i className="icon ion-md-eye"/>
                                    </button>
                                ],
                                onClick: this.selectNode(rowInfo),
                                className: "unselected-node"
                            }
                            if (nodeSelected) {
                                nodeProps.className = "selected-node";
                            } else if (nodeHidden) {
                                nodeProps.className = "hidden-node";
                            }
                            return nodeProps;
                        }}/>
                </div>
            </div>
        );
    }
}

const Pending = () => {
    return <div>
               <div className="row justify-content-center"><span>Generating model structure...</span></div>
               <div className="row justify-content-center"><i className="icon ion-md-hourglass wait-spinner" /></div></div>;
}

const NoStructure = () => {
    return <div>
               <div className="row justify-content-center"><span>Model has no structure definition</span></div></div>;
}

export { ModelStructure, Pending, NoStructure }
